import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { EventCateringApprovalListItemDTO } from '../../../dto/event/event-catering-approval-list-item.dto';
import {
  DunaCateringPreferencesDialogComponent,
  DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
} from '../duna-catering/duna-catering-preferences-dialog.component';
import { environment } from '../../../environment/environment';
import { DunaCateringSummaryWidgetComponent } from '../duna-dashboard/catering-summary-widget/duna-catering-summary-widget.component';
import {
  CateringSummaryData,
  CateringSummaryEvent,
  CATERING_SUMMARY_DAY_OPTIONS,
  CATERING_SUMMARY_DEFAULT_DAYS,
  fetchCateringSummaryForUniqueLink,
} from '../../../services/dashboard/data/catering-summary.data';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-duna-catering-unique-events',
  standalone: true,
  imports: [
    MatCardModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    DynamicTableComponent,
    DunaCateringSummaryWidgetComponent,
  ],
  templateUrl: './duna-catering-unique-events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering-unique-events.component.scss',
})
export class DunaCateringUniqueEventsComponent implements OnInit, OnDestroy {
  @ViewChild('pendingTable') pendingTable?: DynamicTableComponent;
  @ViewChild('approvedTable') approvedTable?: DynamicTableComponent;

  readonly dayOptions = CATERING_SUMMARY_DAY_OPTIONS;

  /** Udledes fra URL; kun gyldigt GUID-format – eksistens tjekkes mod API. */
  linkUuid: string | null = null;
  /** Når sat vises fejl i stedet for tabeller (ugyldig URL, ukendt uuid eller netværksfejl). */
  linkErrorMessage: string | null = null;
  linkValidationPending = false;

  cateringSummaryData: CateringSummaryData | null = null;
  cateringSummaryLoading = false;
  cateringSummaryDays = CATERING_SUMMARY_DEFAULT_DAYS;

  private destroyed = false;
  private validationAbort: AbortController | null = null;
  private summaryAbort: AbortController | null = null;
  private preferencesListener = (e: Event) =>
    this.handlePreferencesEvent(e as CustomEvent);

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const urlWithoutQuery = (this.router.url || '').split('?')[0];
    this.linkUuid =
      urlWithoutQuery.split('/').find((segment) => UUID_REGEX.test(segment)) ||
      null;

    window.addEventListener(
      'duna-catering-preferences',
      this.preferencesListener as EventListener
    );

    if (!this.linkUuid) {
      this.linkErrorMessage =
        'Der blev ikke fundet et gyldigt forplejningslink i adressen. Et korrekt link ser ud som /catering/efterfulgt af et UUID.';
      this.cdr.detectChanges();
      return;
    }

    this.validateLinkExists(this.linkUuid);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.validationAbort?.abort();
    this.summaryAbort?.abort();
    window.removeEventListener(
      'duna-catering-preferences',
      this.preferencesListener as EventListener
    );
  }

  /** Sandt når der må vises de to dynamiske tabeller (kendt link). */
  get showEventTables(): boolean {
    return (
      !!this.linkUuid && !this.linkErrorMessage && !this.linkValidationPending
    );
  }

  setCateringSummaryDays(days: number): void {
    if (!this.dayOptions.includes(days as (typeof this.dayOptions)[number])) {
      return;
    }
    if (this.cateringSummaryDays === days || !this.linkUuid) {
      return;
    }
    this.cateringSummaryDays = days;
    this.loadCateringSummary();
  }

  dayOptionLabel(days: number): string {
    if (days === 1) return '1 dag';
    if (days === 7) return '1 uge';
    if (days === 14) return '14 dage';
    if (days === 30) return '1 måned';
    return `${days} dage`;
  }

  openCateringPreferences(event: CateringSummaryEvent): void {
    if (!event?.guid) return;
    const row: EventCateringApprovalListItemDTO = {
      event_id: event.event_id,
      guid: event.guid,
      event_type: 'exercise',
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      event_name: event.event_name,
      location: event.location,
      current_pending_user_count:
        event.current_pending_user_count ?? event.participant_count,
      approved_pending_user_count: event.approved_pending_user_count ?? null,
      approved_at: event.approved_at ?? null,
      approval_status: event.approval_status || 'pending',
    };
    this.dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      data: { row },
    });
  }

  /**
   * Bruger fetch (ikke HttpClient) så kaldet ikke blokeres i auth-interceptorens token-kæde,
   * som på offentlige /catering/:uuid-sider kan holde observables åbne uden svar.
   */
  private validateLinkExists(uuid: string): void {
    this.validationAbort?.abort();
    this.validationAbort = new AbortController();
    const signal = this.validationAbort.signal;

    let timedOut = false;
    const timeoutMs = 20000;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      this.validationAbort?.abort();
    }, timeoutMs);

    this.linkValidationPending = true;
    this.linkErrorMessage = null;
    this.cdr.detectChanges();

    const qs = new URLSearchParams({
      page: '1',
      pageSize: '1',
      filter_include_no_participants: 'true',
    });
    const url = `${
      environment.apiUrl
    }/events/catering/unique-links/${encodeURIComponent(
      uuid
    )}/events?${qs.toString()}`;

    fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal,
    })
      .then(async (res) => {
        window.clearTimeout(timeoutId);
        if (this.destroyed) return;

        let bodyText = '';
        try {
          bodyText = await res.text();
        } catch {
          bodyText = '';
        }

        const parsed = this.tryParseRecordFromBody(bodyText);

        const rawMsg = parsed?.['message'];
        const rawDetail = parsed?.['detail'];
        const apiMessage = typeof rawMsg === 'string' ? rawMsg.trim() : '';
        const apiDetail = typeof rawDetail === 'string' ? rawDetail.trim() : '';
        const combinedApiText = `${apiMessage} ${apiDetail}`.toLowerCase();

        const looksLikeLinkMissing =
          res.status === 404 ||
          /\blink\s+not\s+found\b/i.test(apiMessage) ||
          /\blink\s+not\s+found\b/i.test(combinedApiText);

        if (looksLikeLinkMissing) {
          this.linkErrorMessage =
            'Dette forplejningslink findes ikke eller er ikke længere gyldigt. Tjek at du har kopieret hele linket, eller kontakt din administrator.';
          return;
        }

        if (res.status === 400) {
          this.linkErrorMessage =
            apiMessage ||
            'Linket i adressen er ikke et gyldigt UUID. Et korrekt link ser ud som /catering/efterfulgt af et UUID.';
          return;
        }

        if (!res.ok) {
          this.linkErrorMessage =
            apiMessage ||
            apiDetail ||
            'Linket kunne ikke bekræfte mod serveren. Prøv igen senere, eller kontakt din administrator hvis problemet fortsætter.';
          return;
        }

        const meta = parsed?.['meta'];
        if (parsed !== null && typeof meta === 'object' && meta !== null) {
          this.loadCateringSummary();
          return;
        }

        this.linkErrorMessage =
          parsed === null
            ? 'Der kom et uventet svar fra serveren (kunne ikke læse JSON — evt. HTML eller PHP-advarsler foran svaret). Linket kan stadig være gyldigt; prøv at genindlæse eller kontakt administrator.'
            : 'Forplejningslinket kunne ikke bekræftes korrekt mod serveren (svar manglede forventet liste-format). Prøv igen senere eller kontakt din administrator.';
      })
      .catch((err: unknown) => {
        window.clearTimeout(timeoutId);
        if (this.destroyed) return;

        const name =
          err && typeof err === 'object' && 'name' in err
            ? String((err as { name: string }).name)
            : '';
        if (name === 'AbortError') {
          if (timedOut) {
            this.linkErrorMessage = `Serveren svarede ikke inden for ${
              timeoutMs / 1000
            } sekunder. Tjek din forbindelse og prøv igen.`;
          } else {
            this.linkErrorMessage =
              'Forbindelsen blev afbrudt før serveren svarede. Genindlæs siden — forplejningslinket kan stadig være ugyldigt eller udløbet.';
          }
          return;
        }
        this.linkErrorMessage =
          'Linket kunne ikke bekræfte mod serveren (nettet eller CORS blokerede måske kaldet). Prøv igen senere, eller kontakt din administrator.';
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (!this.destroyed) {
          this.linkValidationPending = false;
          this.cdr.detectChanges();
        }
      });
  }

  private async loadCateringSummary(): Promise<void> {
    if (!this.linkUuid || this.destroyed) return;

    this.summaryAbort?.abort();
    this.summaryAbort = new AbortController();
    const signal = this.summaryAbort.signal;

    this.cateringSummaryLoading = true;
    this.cdr.detectChanges();

    try {
      if (signal.aborted) return;
      const summary = await fetchCateringSummaryForUniqueLink(
        this.linkUuid,
        this.cateringSummaryDays
      );
      if (this.destroyed || signal.aborted) return;
      this.cateringSummaryData = summary;
    } finally {
      if (!this.destroyed && !signal.aborted) {
        this.cateringSummaryLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * API kan returnere ren JSON eller HTML/advarsler fulgt af JSON — samme strategi som parseApiPayload i catering-wishes.
   */
  private tryParseRecordFromBody(
    rawBody: string
  ): Record<string, unknown> | null {
    const trimmed = rawBody.trim();
    if (!trimmed) return null;

    const candidates: string[] = [trimmed];
    const firstObjectStart = trimmed.indexOf('{');
    const lastObjectEnd = trimmed.lastIndexOf('}');
    if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
      candidates.push(trimmed.slice(firstObjectStart, lastObjectEnd + 1));
    }

    const trailingJsonMatch = trimmed.match(/\{[\s\S]*\}\s*$/);
    if (trailingJsonMatch?.[0]) {
      candidates.push(trailingJsonMatch[0].trim());
    }

    for (const candidate of Array.from(new Set(candidates))) {
      try {
        const value = JSON.parse(candidate) as unknown;
        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          return value as Record<string, unknown>;
        }
      } catch {
        /* prøv næste kandidat */
      }
    }

    return null;
  }

  private handlePreferencesEvent(event: CustomEvent): void {
    const row = event.detail as EventCateringApprovalListItemDTO;
    if (!row) return;
    this.dialog.open(DunaCateringPreferencesDialogComponent, {
      ...DUNA_CATERING_PREFERENCES_DIALOG_CONFIG,
      data: { row },
    });
  }
}
